import {
  creativeMenuTools,
  creativeToolPagePath,
  type CreativeTool,
} from "@shared/creativeTools";
import { VIRAL_PROMO_PAGE_PATH } from "@shared/viralPromo";

/** A link in one of the header's list menus: its translation key (globalNav
 *  or landingV2.header), the English fallback, and the path it opens. */
export type NavItem = { key: string; href: string; defaultValue?: string };

// Ad Studio menu: the ad generator, its root presets (creativeToolPresets
// entries with root: true, each served at its own URL), the real estate tour
// and the song promo. Nouns rather than "Create …": the menu is the studio.
export const AD_STUDIO: NavItem[] = [
  { key: "landingV2.header.videoAd", href: creativeToolPagePath("song-promo-ad"), defaultValue: "Video Ad" },
  { key: "landingV2.header.ugcVideo", href: "/ai-ugc-video-generator", defaultValue: "UGC Video" },
  { key: "landingV2.header.productVideo", href: "/ai-product-video-generator", defaultValue: "Product Video" },
  { key: "landingV2.header.commercial", href: "/ai-commercial-generator", defaultValue: "Commercial" },
  { key: "landingV2.header.facebookAd", href: "/ai-facebook-ad-generator", defaultValue: "Facebook Ad" },
  { key: "landingV2.header.realEstateVideo", href: creativeToolPagePath("real-estate-video"), defaultValue: "Real Estate Video" },
  { key: "landingV2.header.viralSongPromo", href: VIRAL_PROMO_PAGE_PATH, defaultValue: "Viral Song Promo" },
];

// Film Studio menu: the short film generator and its micro drama preset.
export const FILM_STUDIO: NavItem[] = [
  { key: "landingV2.header.shortFilm", href: creativeToolPagePath("short-film"), defaultValue: "Short Film" },
  { key: "landingV2.header.microDrama", href: "/ai-micro-drama-generator", defaultValue: "Micro Drama" },
];

/** The tools the studio menus already list, left out of the Tools menu's
 *  Video column so each destination appears once in the header. */
export const STUDIO_TOOL_IDS: ReadonlySet<string> = new Set([
  "song-promo-ad",
  "real-estate-video",
  "short-film",
  "viral-song-promo",
]);

/** The Tools menu's Image or Video column. */
export const toolsMenuItems = (category: "image" | "video"): CreativeTool[] =>
  creativeMenuTools(category).filter((tool) => !STUDIO_TOOL_IDS.has(tool.id));

/** The air HeaderV2's top row puts between its controls, in px. */
export type RowSpacing = {
  /** Between the wordmark and the first nav link. */
  markGap: number;
  /** Each nav link's left (and right) padding. */
  linkPad: number;
  /** Between two nav links. */
  linkGap: number;
  /** Between two account-row controls. */
  buttonGap: number;
};

/** The top row's contents with no spacing: the wordmark (0 with the rail),
 *  each nav link without its padding, each visible account-row control. */
export type RowWidths = { mark: number; links: number[]; buttons: number[] };

/** The least space left between the last nav link and the buttons. */
const LINK_TO_BUTTONS = 16;

export const rowWidth = (
  { mark, links, buttons }: RowWidths,
  s: RowSpacing,
): number => {
  const between = (count: number, gap: number) => gap * Math.max(0, count - 1);
  const sum = (widths: number[]) => widths.reduce((total, w) => total + w, 0);
  return (
    (mark ? mark + s.markGap : 0) +
    sum(links) +
    links.length * 2 * s.linkPad +
    between(links.length, s.linkGap) +
    LINK_TO_BUTTONS +
    sum(buttons) +
    between(buttons.length, s.buttonGap)
  );
};

/** "one": one row as designed; "tight": one row with less air between the
 *  controls (.nav-tight); "row": the nav takes a row of its own (.nav-row). */
export type HeaderLayout = "one" | "tight" | "row";

/** The widths don't depend on the layout chosen, so neither does the answer:
 *  the header can't flip back and forth between two layouts. */
export const headerLayout = (
  widths: RowWidths,
  room: number,
  spacing: { full: RowSpacing; tight: RowSpacing },
): HeaderLayout =>
  rowWidth(widths, spacing.full) <= room
    ? "one"
    : rowWidth(widths, spacing.tight) <= room
      ? "tight"
      : "row";
